import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Form } from 'react-bootstrap';
import { useModal } from '../../../context/ModalContext';
import { changesApi } from '../../../services/changesApi';
import type {
  AgendaKind,
  ChangeAgendaEmailTemplate,
  ChangeAgendaQueuedSendSummaryItem,
  ChangeAgendaRecipient,
  ChangeAgendaSendKind,
  ChangeAgendaSendSummaryItem,
  ChangeMeetingKind,
} from '../../../types/changes';
import { defaultLayoutForAgendaKind } from '../../../types/changes';
import { describeAgendaEmailDispatch } from '../agendaEmailDispatchStatus';
import { getApiErrorMessage } from '../../../utils/apiError';
import AgendaQueuedDispatchList from './AgendaQueuedDispatchList';
import MeetingAgendaComposeModal from './MeetingAgendaComposeModal';

const SEND_GROUPS: Array<{ kind: ChangeAgendaSendKind; label: string }> = [
  { kind: 'committee', label: 'Riscos e Impactos' },
  { kind: 'camud', label: 'CAMUD Go/No-Go' },
];

function meetingAgendaKinds(kind?: ChangeMeetingKind | string | null): ChangeAgendaSendKind[] {
  if (kind === 'camud_go_no_go') return ['camud'];
  if (kind === 'riscos_impactos') return ['committee'];
  return ['committee', 'camud'];
}

function defaultAgendaKindForMeeting(kind?: ChangeMeetingKind | string | null): AgendaKind {
  const kinds = meetingAgendaKinds(kind);
  if (kinds.length === 1) return kinds[0];
  return 'both';
}

function formatScheduledAt(value?: string | null): string {
  if (!value) return '—';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '—';
  return parsed.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

type ScheduleMode = 'now' | 'offset_1' | 'offset_3' | 'custom';

interface Props {
  meetingId: number;
  meetingTitle: string;
  meetingKind?: ChangeMeetingKind | string | null;
  scheduledAt?: string | null;
  itemCount?: number;
  previewChangeId?: number;
  sentItems?: ChangeAgendaSendSummaryItem[];
  queuedItems?: ChangeAgendaQueuedSendSummaryItem[];
  onSent: (message: string) => void;
  onDispatchRefresh?: () => void;
  onComplete?: () => void;
  embedded?: boolean;
}

const CalendarMeetingAgendaPanel: React.FC<Props> = ({
  meetingId,
  meetingTitle,
  meetingKind,
  scheduledAt,
  itemCount = 0,
  previewChangeId,
  sentItems = [],
  queuedItems = [],
  onSent,
  onDispatchRefresh,
  onComplete,
  embedded = true,
}) => {
  const { showConfirm } = useModal();
  const allowedKinds = useMemo(() => meetingAgendaKinds(meetingKind), [meetingKind]);
  const [selected, setSelected] = useState<ChangeAgendaSendKind[]>(allowedKinds);
  const [recipientsByKind, setRecipientsByKind] = useState<
    Partial<Record<ChangeAgendaSendKind, ChangeAgendaRecipient[]>>
  >({});
  const [, setLayouts] = useState<ChangeAgendaEmailTemplate[]>([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [scheduleMode, setScheduleMode] = useState<ScheduleMode>('now');
  const [customSendAt, setCustomSendAt] = useState('');
  const [sendTime, setSendTime] = useState('07:30');
  const [composeOpen, setComposeOpen] = useState(false);

  useEffect(() => {
    setSelected(allowedKinds);
  }, [allowedKinds.join(',')]);

  const loadPreview = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const changeId =
        previewChangeId && previewChangeId > 0 ? previewChangeId : undefined;
      const [layoutResponse, ...recipientResponses] = await Promise.all([
        changesApi.listAgendaEmailLayouts().catch(() => ({ items: [] })),
        ...allowedKinds.map((kind) =>
          changesApi.listAgendaRecipients({
            agenda_kind: kind,
            change_id: changeId,
            limit: 50,
          }),
        ),
      ]);
      setLayouts(layoutResponse.items || []);
      const next: Partial<Record<ChangeAgendaSendKind, ChangeAgendaRecipient[]>> = {};
      allowedKinds.forEach((kind, index) => {
        next[kind] = recipientResponses[index]?.items || [];
      });
      setRecipientsByKind(next);
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Falha ao carregar destinatários.'));
    } finally {
      setLoading(false);
    }
  }, [allowedKinds, previewChangeId]);

  useEffect(() => {
    void loadPreview();
  }, [loadPreview]);

  const toggleKind = (kind: ChangeAgendaSendKind, checked: boolean) => {
    setSelected((prev) =>
      checked ? Array.from(new Set([...prev, kind])) : prev.filter((value) => value !== kind),
    );
  };

  const selectedPeopleCount = useMemo(() => {
    const emails = new Set<string>();
    for (const kind of selected) {
      for (const person of recipientsByKind[kind] || []) {
        if (person.email) emails.add(person.email.toLowerCase());
      }
    }
    return emails.size;
  }, [recipientsByKind, selected]);

  const resolveAgendaKind = (): AgendaKind => {
    if (selected.length === 2) return 'both';
    if (selected.length === 1) return selected[0];
    return defaultAgendaKindForMeeting(meetingKind);
  };

  const dispatchStatus = describeAgendaEmailDispatch({ sentItems, queuedItems });

  const handleSend = async (custom?: { subject: string; bodyHtml: string; files?: File[] }) => {
    if (!selected.length) {
      setError('Marque ao menos um grupo de comitê.');
      return;
    }
    const labels = SEND_GROUPS.filter((group) => selected.includes(group.kind)).map((g) => g.label);
    const whenLabel = formatScheduledAt(scheduledAt);
    const scheduleLabel =
      scheduleMode === 'now'
        ? 'agora (fila assíncrona de e-mail)'
        : scheduleMode === 'offset_1'
          ? '1 dia antes da reunião'
          : scheduleMode === 'offset_3'
            ? '3 dias antes da reunião'
            : customSendAt
              ? `em ${customSendAt}`
              : 'data personalizada';

    const confirmed = await showConfirm({
      title: custom ? 'Enviar este texto da reunião' : 'Enviar e-mail da reunião',
      message: `Enviar e-mail de «${meetingTitle}» (${whenLabel})?`,
      details: `${dispatchStatus.modalParagraph} Novo disparo: ${itemCount} item(ns) na pauta · Grupos: ${labels.join(', ')} · ${selectedPeopleCount} destinatário(s) · ${scheduleLabel}${custom ? ' · texto escrito nesta reunião' : ''}${custom?.files?.length ? ` · ${custom.files.length} anexo(s)` : ''}.`,
      variant: 'primary',
      confirmLabel: 'Enviar e-mail',
      cancelLabel: 'Cancelar',
    });
    if (!confirmed) return;

    setSending(true);
    setError(null);
    try {
      const agendaKind = resolveAgendaKind();
      const layoutKey = defaultLayoutForAgendaKind(
        agendaKind === 'both' ? 'committee' : agendaKind,
      );
      const payload: Parameters<typeof changesApi.sendMeetingAgenda>[1] = {
        agenda_kind: agendaKind,
        layout: layoutKey,
      };
      if (custom) {
        payload.use_custom_layout = true;
        payload.custom_subject = custom.subject;
        payload.custom_body_html = custom.bodyHtml;
      }
      if (scheduleMode === 'offset_1') {
        payload.offset_days_before = 1;
        payload.send_time = sendTime;
      } else if (scheduleMode === 'offset_3') {
        payload.offset_days_before = 3;
        payload.send_time = sendTime;
      } else if (scheduleMode === 'custom' && customSendAt) {
        payload.send_at = customSendAt;
      }

      const result = await changesApi.sendMeetingAgenda(meetingId, payload, custom?.files);
      setComposeOpen(false);
      if (result.outcome === 'queued' || result.queued) {
        onDispatchRefresh?.();
        onSent(
          `E-mail agendado para ${result.send_at || customSendAt}. A fila dispara sem travar a tela.`,
        );
        return;
      }
      if (result.changes === 0) {
        setError('A pauta da reunião está vazia. Inclua mudanças antes de enviar.');
        return;
      }
      if (result.sent === 0) {
        setError('E-mail montado, mas nenhum destinatário válido foi encontrado nos comitês.');
        return;
      }
      onDispatchRefresh?.();
      onSent(
        `E-mail enviado: ${result.sent} destinatário(s), ${result.changes} mudança(s) na pauta. Registro #${result.send_log_id}.`,
      );
      onComplete?.();
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Falha ao enviar e-mail.'));
    } finally {
      setSending(false);
    }
  };

  const wrapperClass = embedded ? 'border-top pt-2 mt-2' : '';
  const composeKind = resolveAgendaKind();
  const composeLayout = defaultLayoutForAgendaKind(composeKind === 'both' ? 'committee' : composeKind);

  return (
    <div className={wrapperClass}>
      <div className="small text-muted mb-2">
        O e-mail inclui <strong>título</strong>, <strong>data/horário</strong> da reunião e a{' '}
        <strong>lista da pauta</strong>. Use <strong>Escrever e-mail</strong> para puxar o layout de
        Cadastros → E-mail da pauta já preenchido com as N mudanças desta reunião. Placeholders:
        {' {{reuniao.titulo}}'}, {'{{reuniao.horario}}'}, {'{{reuniao.tipo}}'}.
      </div>
      <div className="small mb-2">
        <strong>Reunião:</strong> {meetingTitle} · <strong>Quando:</strong>{' '}
        {formatScheduledAt(scheduledAt)} · <strong>Pauta:</strong>{' '}
        {itemCount === 1 ? '1 mudança' : `${itemCount} mudanças`}
      </div>
      <Alert variant={dispatchStatus.hasHistory ? 'warning' : 'info'} className="py-2 small mb-2">
        {dispatchStatus.headline}
      </Alert>
      <AgendaQueuedDispatchList
        items={queuedItems}
        onChanged={() => onDispatchRefresh?.()}
        onInfo={onSent}
      />
      {error ? (
        <Alert variant="danger" className="py-2" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      ) : null}
      <Form.Group className="mb-2">
        <Form.Label className="small fw-semibold">Quando disparar</Form.Label>
        <div className="d-flex flex-wrap gap-3">
          <Form.Check
            type="radio"
            id={`meet-send-now-${meetingId}`}
            name={`meet-schedule-${meetingId}`}
            label="Agora"
            checked={scheduleMode === 'now'}
            onChange={() => setScheduleMode('now')}
          />
          <Form.Check
            type="radio"
            id={`meet-send-1d-${meetingId}`}
            name={`meet-schedule-${meetingId}`}
            label="1 dia antes"
            checked={scheduleMode === 'offset_1'}
            onChange={() => setScheduleMode('offset_1')}
          />
          <Form.Check
            type="radio"
            id={`meet-send-3d-${meetingId}`}
            name={`meet-schedule-${meetingId}`}
            label="3 dias antes"
            checked={scheduleMode === 'offset_3'}
            onChange={() => setScheduleMode('offset_3')}
          />
          <Form.Check
            type="radio"
            id={`meet-send-custom-${meetingId}`}
            name={`meet-schedule-${meetingId}`}
            label="Data/hora exata"
            checked={scheduleMode === 'custom'}
            onChange={() => setScheduleMode('custom')}
          />
        </div>
        {scheduleMode === 'offset_1' || scheduleMode === 'offset_3' ? (
          <Form.Control
            type="time"
            className="mt-2"
            style={{ maxWidth: 140 }}
            value={sendTime}
            onChange={(e) => setSendTime(e.target.value)}
            aria-label="Horário do disparo antes da reunião"
          />
        ) : null}
        {scheduleMode === 'custom' ? (
          <Form.Control
            type="datetime-local"
            className="mt-2"
            style={{ maxWidth: 260 }}
            value={customSendAt}
            onChange={(e) => setCustomSendAt(e.target.value)}
            aria-label="Data e hora do envio"
          />
        ) : null}
      </Form.Group>
      {loading ? <div className="text-muted small mb-2">Carregando grupos e destinatários…</div> : null}
      {SEND_GROUPS.filter((g) => allowedKinds.includes(g.kind)).map((group) => {
        const people = recipientsByKind[group.kind] || [];
        return (
          <div key={group.kind} className="border rounded p-2 mb-2 bg-white">
            <Form.Check
              type="checkbox"
              id={`meet-agenda-${meetingId}-${group.kind}`}
              label={group.label}
              checked={selected.includes(group.kind)}
              onChange={(event) => toggleKind(group.kind, event.target.checked)}
            />
            {people.length === 0 && !loading ? (
              <div className="small text-muted mt-1">Nenhuma pessoa ativa neste comitê.</div>
            ) : (
              <ul className="small mb-0 mt-1 ps-3">
                {people.map((person) => (
                  <li key={`${group.kind}-${person.person_id || person.email}`}>
                    {person.name} · {person.email}
                  </li>
                ))}
              </ul>
            )}
          </div>
        );
      })}
      <div className="d-flex flex-wrap gap-2">
        <Button
          size="sm"
          variant="outline-primary"
          disabled={sending || loading || selected.length === 0}
          onClick={() => setComposeOpen(true)}
        >
          Escrever e-mail
        </Button>
        <Button size="sm" disabled={sending || loading || selected.length === 0} onClick={() => void handleSend()}>
          Enviar e-mail
        </Button>
      </div>
      <MeetingAgendaComposeModal
        show={composeOpen}
        meetingId={meetingId}
        agendaKind={composeKind}
        layout={composeLayout}
        sending={sending}
        onHide={() => setComposeOpen(false)}
        onSend={(payload) => void handleSend(payload)}
      />
    </div>
  );
};

export default CalendarMeetingAgendaPanel;
